import { faBox, faTrashCan } from '@fortawesome/pro-regular-svg-icons'
import {
  Avatar,
  Button,
  Center,
  Stack,
  Text,
  NumberInput,
  TextInput,
  Flex,
  Checkbox,
} from '@mantine/core'
import { useState } from 'react'

import { Trans, useTranslation } from '@flecto/translations'
import { type ParameterID, type CoreBundleItemPayload } from '@flecto/types'
import { AvatarItem, DataTable, type DataTableColumn, Icon } from '@flecto/ui'

import { useFormContext } from './FormBundle'

type TableBundleItemsProps = {
  bundleItems: Array<CoreBundleItemPayload>
}

function RemoveButton({ itemId }: { itemId: ParameterID }) {
  const { t } = useTranslation('saas', { keyPrefix: 'items' })
  const form = useFormContext()

  const handleRemoveItem = () => {
    const updatedItems = form.values.bundle_items.map((item) =>
      item.item_id === itemId ? { ...item, _destroy: true } : item
    )

    form.setFieldValue('bundle_items', updatedItems)
  }

  return (
    <Button
      variant='outline'
      color='red.7'
      size='sm'
      fw={600}
      onClick={handleRemoveItem}
      leftSection={<Icon icon={faTrashCan} />}
    >
      {t('new_bundle_remove')}
    </Button>
  )
}

function TableBundleItemsEmptyState() {
  const { t } = useTranslation('saas', { keyPrefix: 'items' })

  return (
    <Center p='xl'>
      <Stack align='center' gap='sm'>
        <Avatar size='md' bg='gray.1' radius='50%'>
          <Icon icon={faBox} c='gray.7' />
        </Avatar>
        <Text c='gray.8' fw={500}>
          {t('new_bundle_no_items_title')}
        </Text>
        <Text c='gray.7' fw={400} ta='center'>
          <Trans i18nKey='new_bundle_no_items_description' t={t} />
        </Text>
      </Stack>
    </Center>
  )
}

export default function TableBundleItems({
  bundleItems,
  ...props
}: TableBundleItemsProps) {
  const form = useFormContext()
  const { t } = useTranslation('saas', { keyPrefix: 'items' })

  const [isGeneralDiscountEnabled, setIsGeneralDiscountEnabled] =
    useState(false)
  const [generalDiscountValue, setGeneralDiscountValue] = useState(0)
  const [originalDiscounts, setOriginalDiscounts] = useState<
    Record<ParameterID, number>
  >({})

  const handleQuantityChange = (
    value: string | number,
    itemId: ParameterID
  ) => {
    const numericValue = Number(value)

    if (isNaN(numericValue)) return

    const itemIndex = form.values.bundle_items.findIndex(
      (item) => item.item_id === itemId
    )

    if (itemIndex !== -1) {
      form.setFieldValue(`bundle_items.${itemIndex}.quantity`, numericValue)
      form.values.bundle_items[itemIndex].quantity = numericValue
    }
  }

  const handleDiscountChange = (value: string, itemId: ParameterID) => {
    const numericValue = parseFloat(value)

    if (isNaN(numericValue)) return

    const itemIndex = form.values.bundle_items.findIndex(
      (item) => item.item_id === itemId
    )

    if (itemIndex !== -1) {
      form.setFieldValue(
        `bundle_items.${itemIndex}.discount_amount`,
        numericValue
      )
    }
  }

  const handleGeneralDiscountToggle = (checked: boolean) => {
    setIsGeneralDiscountEnabled(checked)

    if (checked) {
      const discounts: Record<ParameterID, number> = {}
      form.values.bundle_items.forEach((item) => {
        discounts[item.item_id] = item.discount_amount ?? 0
      })
      setOriginalDiscounts(discounts)

      applyGeneralDiscount(generalDiscountValue)
    } else {
      form.values.bundle_items.forEach((item, index) => {
        if (originalDiscounts[item.item_id] !== undefined) {
          form.setFieldValue(
            `bundle_items.${index}.discount_amount`,
            originalDiscounts[item.item_id]
          )
        }
      })
    }
  }

  const applyGeneralDiscount = (value: number | string) => {
    form.values.bundle_items.forEach((_, index) => {
      form.setFieldValue(`bundle_items.${index}.discount_amount`, Number(value))
    })
  }

  const handleGeneralDiscountChange = (value: number | string) => {
    const numericValue = Number(value)
    if (isNaN(numericValue)) return

    setGeneralDiscountValue(numericValue)

    if (isGeneralDiscountEnabled) {
      applyGeneralDiscount(numericValue)
    }
  }

  const columns: DataTableColumn<CoreBundleItemPayload>[] = [
    {
      accessor: 'item',
      cellsStyle: () => ({ height: '78px' }),
      render: (row) => {
        if (row.item_id === 'discountRow') {
          return (
            <Flex>
              <Checkbox
                checked={isGeneralDiscountEnabled}
                onChange={(event) =>
                  handleGeneralDiscountToggle(event.currentTarget.checked)
                }
                label={
                  <Text fw={500}>{t('new_bundle_items_apply_discount')}</Text>
                }
                color='green.7'
              />
            </Flex>
          )
        }

        return (
          <Flex align='center' gap='md'>
            <AvatarItem item={row} />
            <Text fw={500} truncate>
              {row?.title}
            </Text>
          </Flex>
        )
      },
      title: 'Items',
      width: 400,
    },
    {
      accessor: 'quantity',
      cellsStyle: () => ({ position: 'relative' }),
      render: (row) =>
        row.item_id !== 'discountRow' && (
          <Flex direction='column'>
            <NumberInput
              min={0}
              step={1}
              style={{ maxWidth: '127px' }}
              size='sm'
              max={
                row.allow_overbooking || !row.track_quantity
                  ? undefined
                  : row.quantity_available
              }
              value={row.quantity}
              onChange={(value) => handleQuantityChange(value, row.item_id)}
            />
            <Text
              c={'gray.7'}
              size='sm'
              style={{ position: 'absolute', bottom: '4px' }}
            >
              {row.allow_overbooking && !row.track_quantity
                ? t('new_bundle_items_unlimited')
                : t('new_bundle_items_availability', {
                    count: row.quantity_available,
                    replace: { count: row.quantity_available },
                  })}
            </Text>
          </Flex>
        ),
      title: 'Quantity',
      width: 160,
    },
    {
      accessor: 'discount',
      render: (row) => {
        if (row.item_id === 'discountRow') {
          return (
            <TextInput
              size='sm'
              style={{ maxWidth: '127px' }}
              leftSection={
                <Text c='gray.6' mr='4px'>
                  %
                </Text>
              }
              value={generalDiscountValue}
              onChange={(event) =>
                handleGeneralDiscountChange(event.currentTarget.value)
              }
              disabled={!isGeneralDiscountEnabled}
            />
          )
        }

        return (
          <TextInput
            size='sm'
            style={{ maxWidth: '127px' }}
            leftSection={
              <Text c='gray.6' mr='4px'>
                %
              </Text>
            }
            value={row.discount_amount}
            onChange={(event) =>
              handleDiscountChange(event.currentTarget.value, row.item_id)
            }
            disabled={isGeneralDiscountEnabled}
          />
        )
      },
      title: 'Discount',
      width: 160,
    },
    {
      accessor: 'actions',
      render: (row) =>
        row.item_id !== 'discountRow' && <RemoveButton itemId={row.item_id} />,
      title: '',
      width: 130,
    },
  ]

  const discountRow: CoreBundleItemPayload = {
    item_id: 'discountRow',
    quantity: 0,
    _destroy: false,
  }

  return (
    <DataTable
      {...props}
      columns={columns}
      records={
        bundleItems.length > 0 ? [...bundleItems, discountRow] : bundleItems
      }
      emptyState={<TableBundleItemsEmptyState />}
    />
  )
}
